import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  InputAdornment,
  CircularProgress,
  Typography,
  Box,
  Paper,
  TextField,
  Avatar,
  Divider,
  useTheme,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Search, FileText, Check } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllManifests,
  addLegToManifest,
  createManifest,
  updateOrderStatus,
} from "src/queries/query";
import { OrderStatus } from "src/constants/OrderStatus";
import { toast } from "react-toastify";
import ManifestStartTimePicker from "src/components/apps/ManifestsComponents/ManifestStartTimePicker";

export default function AddToExistingManifestModal({
  open,
  handleClose,
  legs = [], // Provide a default empty array for legs
  onAssign, // Keep for potential future use or remove if not needed
  refetchOrders, // Keep for potential future use or remove if not needed
  isOSND = false,
  forceLineHaul = false,
}) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [dateFilter, setDateFilter] = useState("all"); // all, today, yesterday, lastWeek
  const [startTime, setStartTime] = useState(null);

  // Check if any leg is lineHaul (or forced via prop for warehouse picking flow)
  const isLineHaul =
    forceLineHaul || legs.some((leg) => leg?.isLineHaul);

  const {
    data: manifestListData,
    isLoading: isLoadingManifests,
    error: manifestsError,
  } = useQuery({
    queryKey: ["allManifests", isLineHaul],
    queryFn: () =>
      getAllManifests({
        params: isLineHaul
          ? "?page=1&limit=500&isLineHaul=true"
          : "?page=1&limit=500",
      }),
    enabled: open, // Only fetch when the modal is open
    select: (data) => (Array.isArray(data?.data?.data) ? data?.data?.data : []),
  });

  const manifestList = manifestListData || [];
  const loading = isLoadingManifests; // For backward compatibility with existing loading variable

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedManifest(null);
      setStartTime(null);
    }
  }, [open]);

  // Calculate total stops and weight for each manifest
  const manifestStats = useMemo(() => {
    const stats = {};
    manifestList.forEach((manifest) => {
      stats[manifest.uuid] = {
        totalStops: manifest?.totalTasks || 0,
        totalWeight: manifest?.totalWeight || 0,
      };
    });
    return stats;
  }, [manifestList]);

  const filteredManifests = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const lastWeekStart = new Date(todayStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    return manifestList.filter((manifest) => {
      // Search filter
      const manifestName =
        manifest?.manifestName || manifest?.manifestNumber || "";
      const manifestNumber =
        typeof manifestName === "string" ? manifestName.toLowerCase() : "";
      const carrierName =
        typeof manifest?.carrier?.name === "string"
          ? manifest.carrier.name.toLowerCase()
          : "";
      const driverName =
        typeof manifest?.driver?.firstName === "string" &&
        typeof manifest?.driver?.lastName === "string"
          ? `${manifest.driver.firstName} ${manifest.driver.lastName}`.toLowerCase()
          : "";

      const matchesSearch =
        manifestNumber.includes(lowerSearch) ||
        carrierName.includes(lowerSearch) ||
        driverName.includes(lowerSearch);

      // Date filter
      let matchesDate = true;
      if (dateFilter !== "all") {
        const manifestDate = manifest?.createdAt
          ? new Date(manifest.createdAt)
          : null;

        if (manifestDate) {
          if (dateFilter === "today") {
            matchesDate = manifestDate >= todayStart;
          } else if (dateFilter === "yesterday") {
            matchesDate =
              manifestDate >= yesterdayStart && manifestDate < todayStart;
          } else if (dateFilter === "lastWeek") {
            matchesDate = manifestDate >= lastWeekStart;
          }
        } else {
          matchesDate = false;
        }
      }

      return matchesSearch && matchesDate;
    });
  }, [manifestList, searchTerm, dateFilter]);

  const addLegMutation = useMutation({
    mutationFn: async ({ manifestUuid, legUuid }) => {
      return addLegToManifest(manifestUuid, legUuid);
    },
    onSuccess: async (data, variables) => {
      toast.success("Leg added to manifest successfully!");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["allManifests"] });
      queryClient.invalidateQueries({
        queryKey: ["manifest", variables.manifestUuid],
      });
      if (refetchOrders) {
        refetchOrders();
      }

      // Update order status to "in-transit" when leg is added to manifest
      const leg = legs.find((l) => l.uuid === variables.legUuid);
      if (leg?.orderStop?.order?.uuid) {
        try {
          await updateOrderStatus(
            leg.orderStop.order.uuid,
            OrderStatus.INTRANSIT,
          );
        } catch (error) {
          console.error("Failed to update order status:", error);
        }
      }
      // Don't call onAssign to avoid duplicate API call
      // handleClose(); // Keep modal open for multiple additions
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to add leg to manifest");
      console.error("Error assigning leg to manifest:", error);
    },
  });

  const createManifestAndAddLegsMutation = useMutation({
    mutationFn: async ({ legUuids, startTime: createStartTime }) => {
      const newManifest = await createManifest(
        legUuids[0],
        isOSND,
        isLineHaul,
        createStartTime || undefined,
      );
      if (legUuids.length > 1) {
        // Add remaining legs to the new manifest
        for (let i = 1; i < legUuids.length; i++) {
          await addLegToManifest(newManifest.uuid, legUuids[i]);
        }
      }
      return newManifest;
    },
    onSuccess: (data) => {
      toast.success("New manifest created and leg(s) added successfully!");
      queryClient.invalidateQueries({ queryKey: ["allManifests"] });
      queryClient.invalidateQueries({ queryKey: ["legs"] });
      if (refetchOrders) {
        refetchOrders();
      }
      handleClose();
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to create manifest and add leg(s)");
      console.error("Error creating manifest and adding leg(s):", error);
    },
  });

  const handleAssignToManifest = async () => {
    if (!selectedManifest?.uuid) {
      // If no manifest is selected, create a new one and add the selected legs
      if (legs.length === 0) {
        toast.error("No legs selected to create a manifest.");
        return;
      }
      const legUuids = legs.map((l) => l.uuid);
      createManifestAndAddLegsMutation.mutate({
        legUuids,
        startTime,
      });
    } else {
      // If a manifest is selected, add all selected legs to it
      if (legs.length === 0) {
        toast.error("No legs selected to add to manifest.");
        return;
      }

      let successfulAdditions = 0;
      let failedAdditions = 0;
      const failedLegsMessages = [];

      for (const singleLeg of legs) {
        try {
          await addLegMutation.mutateAsync({
            legUuid: singleLeg.uuid,
            manifestUuid: selectedManifest.uuid,
          });
          successfulAdditions++;
        } catch (error) {
          failedAdditions++;
          const errorMessage =
            error?.message ||
            `Failed to add leg ${singleLeg.legNumber || singleLeg.uuid}`;
          failedLegsMessages.push(
            `Leg ${singleLeg.legNumber || singleLeg.uuid}: ${errorMessage}`,
          );
          // Do not break the loop, continue with other legs
        }
      }

      if (successfulAdditions > 0 && failedAdditions === 0) {
        toast.success(
          `${successfulAdditions} leg(s) added to manifest successfully!`,
        );
        handleClose();
      } else if (successfulAdditions > 0 && failedAdditions > 0) {
        toast.warn(
          `${successfulAdditions} leg(s) added successfully. ${failedAdditions} leg(s) failed: ${failedLegsMessages.join(
            "; ",
          )}`,
        );
        handleClose(); // Close modal if some succeeded
      } else if (failedAdditions === legs.length) {
        // All failed
        toast.error(
          `All ${failedAdditions} leg(s) failed to add to manifest: ${failedLegsMessages.join(
            "; ",
          )}`,
        );
        // Keep modal open for user to review and potentially choose another manifest
      }
    }
  };

  const titleText =
    legs.length > 1
      ? "Add Legs to Manifest"
      : isLineHaul
        ? "Add leg to linehaul manifest"
        : "Add Leg to Manifest";
  const singleLegNumber = legs[0]?.legNumber;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "primary.contrastText",
            color: "primary.main",
            width: 32,
            height: 32,
          }}
        >
          <FileText size={18} />
        </Avatar>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontSize: "1.1rem" }}>
            {titleText}
          </Typography>
          {legs.length === 1 && singleLegNumber && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {singleLegNumber}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, pb: 0, bgcolor: "grey.50" }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <Search size={18} color={theme.palette.text.secondary} />
              <Input
                type="text"
                placeholder={
                  isLineHaul
                    ? "Search linehaul manifests..."
                    : "Search manifests..."
                }
                fullWidth
                disableUnderline
                sx={{
                  ml: 1,
                  fontSize: "0.9rem",
                  "& .MuiInputBase-input": {
                    padding: 0,
                  },
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Paper>
            <FormControl
              sx={{
                minWidth: 150,
                bgcolor: "background.paper",
                borderRadius: 1.5,
              }}
            >
              <Select
                variant="outlined"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                size="small"
                sx={{
                  height: 45,
                  borderRadius: 1.5,
                  "& .MuiSelect-select": {
                    py: 1,
                  },
                }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="lastWeek">Last Week</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 2.5, minHeight: 350, maxHeight: 400, overflowY: "auto" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 250,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: "1rem" }}
              >
                {searchTerm
                  ? "Search Results"
                  : isLineHaul
                    ? "Available LineHaul Manifests"
                    : "Available Manifests"}
                {!searchTerm && (
                  <Chip
                    label={filteredManifests.length}
                    size="small"
                    sx={{ ml: 1.5, height: 20 }}
                  />
                )}
              </Typography>
              <Box>
                {filteredManifests.length > 0 ? (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="medium" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.100" }}>
                          <TableCell sx={{ width: 50 }}></TableCell>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            Manifest
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            Driver/Carrier
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            Stops
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            Weight
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                          >
                            Legs
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredManifests.map((manifest) => (
                          <TableRow
                            key={manifest.uuid}
                            hover
                            selected={selectedManifest?.uuid === manifest.uuid}
                            onClick={() => {
                              if (selectedManifest?.uuid === manifest.uuid) {
                                setSelectedManifest(null);
                              } else {
                                setSelectedManifest(manifest);
                              }
                            }}
                            sx={{
                              cursor: "pointer",
                              "&.Mui-selected": {
                                bgcolor: "primary.50",
                                border: "2px solid",
                                borderColor: "primary.main",
                              },
                              "&.Mui-selected:hover": {
                                bgcolor: "primary.100",
                                border: "2px solid",
                                borderColor: "primary.dark",
                              },
                            }}
                          >
                            <TableCell sx={{ width: 50, p: 0, pl: 1 }}>
                              {selectedManifest?.uuid === manifest.uuid && (
                                <Check
                                  size={18}
                                  color={theme.palette.primary.main}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, fontSize: "0.85rem" }}
                              >
                                {manifest?.manifestName ||
                                  manifest?.manifestNumber ||
                                  "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {manifest?.driver?.firstName
                                  ? `${manifest?.driver?.firstName} ${manifest?.driver?.lastName}`
                                  : manifest?.carrier?.name || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {manifestStats[manifest.uuid]?.totalStops || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {manifestStats[manifest.uuid]?.totalWeight || 0}{" "}
                                lbs
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={manifest?.status || "N/A"}
                                size="small"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 20,
                                  backgroundColor:
                                    manifest?.status === "Active"
                                      ? "success.light"
                                      : "grey.200",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {manifest?.legs?.length ?? 0}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      color: "text.secondary",
                      display: "flex",
                      gap: 2,
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FileText size={40} sx={{ mb: 1.5, opacity: 0.3 }} />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ fontSize: "1rem" }}
                    >
                      {searchTerm
                        ? "No manifests match your search criteria"
                        : "No manifests available"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, fontSize: "0.85rem" }}
                    >
                      {searchTerm && "Try adjusting your search terms"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2.5,
          bgcolor: "grey.50",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        }}
      >
        {!selectedManifest && (
          <ManifestStartTimePicker
            label="Start Date & Time (optional)"
            value={startTime}
            onChange={setStartTime}
            showClear
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={handleClose}
            size="medium"
            color="error"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignToManifest}
            variant="contained"
            size="medium"
            disabled={
              addLegMutation.isPending ||
              createManifestAndAddLegsMutation.isPending
            }
            sx={{
              minWidth: 140,
              px: 3,
              py: 0.75,
            }}
          >
            {addLegMutation.isPending ||
            createManifestAndAddLegsMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : selectedManifest ? (
              "Add to Manifest"
            ) : (
              "Create New"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
